import { useEffect, useState, type ReactElement } from 'react';
import { isTaskActive } from '@gr/core/platform/tasks/dto';
import { useServices, useT } from '../../../app/di';
import { useResource } from '../../../shared/hooks';
import { useObservable } from '../../../shared/store';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { SegmentedControl } from '../../../ui-kit/Choice/Choice';
import { EmptyState } from '../../../ui-kit/EmptyState/EmptyState';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { TaskList } from './TaskList/TaskList';
import { useTaskActions } from './use-task-actions';

type Filter = 'active' | 'all';

// TASK-UC-6, TASK-RULE-3: задачи всех блогов и платформы; изменения приходят по соединению событий.
export function AdminTasksPage(): ReactElement {
  const { api, tasks } = useServices();
  const { t, te } = useT();
  const [filter, setFilter] = useState<Filter>('active');
  const all = useObservable(tasks.tasks);
  const actions = useTaskActions();
  const loaded = useResource(() => api.tasks.listAll(), [api]);
  useEffect(() => {
    if (loaded.data) tasks.merge(loaded.data);
  }, [loaded.data, tasks]);

  const shown = [...all.values()].filter((task) => filter === 'all' || isTaskActive(task.status)).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const error = loaded.error ?? actions.error;
  return (
    <div className="u-stack-lg">
      <PageHeader title={t('tasks.admin.title')} subtitle={t('tasks.admin.subtitle')} />
      <SegmentedControl<Filter>
        label={t('tasks.admin.filter')}
        options={[
          { value: 'active', label: t('tasks.admin.filters.active') },
          { value: 'all', label: t('tasks.admin.filters.all') },
        ]}
        value={filter}
        onChange={setFilter}
      />
      {error ? <Alert tone="danger">{te(error)}</Alert> : null}
      {loaded.loading && shown.length === 0 ? (
        <Loading label={t('common.loading')} />
      ) : (
        <TaskList
          tasks={shown}
          empty={<EmptyState title={t('tasks.admin.empty')} />}
          showBlog
          canManage={actions.canManage}
          pendingId={actions.pendingId}
          onCancel={(task) => void actions.cancel(task)}
          onResume={(task) => void actions.resume(task)}
        />
      )}
    </div>
  );
}
